import React, {useRef, useState} from "react";
import {
    Box,
    Button,
    Flex,
    Icon,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Text,
    useColorModeValue,
    useToast,
} from "@chakra-ui/react";
import {FaUpload} from "react-icons/fa";
import {useUploadDocumentMutation} from "../../store/patientApi/patientApiSlice.ts";

interface UploadDocumentModalProps {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    patientId?: string;
}

const UploadDocumentModal = (props: UploadDocumentModalProps) => {
    const {open, setOpen} = props;

    const textColor = useColorModeValue("black", "white");
    const inputRef = useRef(null);
    const toast = useToast();

    const [fileName, setFileName] = useState("");
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);


    let passedPatientId = "";
    if (typeof props.patientId !== 'undefined') {
        passedPatientId = props.patientId;
    }

    const [uploadDocument, {isLoading}] = useUploadDocumentMutation();

    const handleUploadClick = () => {
        inputRef.current?.click();
    };

    const handleClose = () => {
        setFileName("")
        setSelectedFile(null);
        setOpen(false);
    }

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files[0];
        if (file) {
            setFileName(file.name);
            setSelectedFile(file);
        }
    };

    const handleCreatePatientReport = async () => {
        if (!selectedFile) {
            toast({
                title: "No file selected.",
                description: "Please upload a document before submitting.",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
            return;
        }
        setIsGenerating(true);
        try {
            const res = await uploadDocument({
                file: selectedFile,
                isExist: Boolean(passedPatientId),
                patientId: passedPatientId,
            }).unwrap();

            toast({
                title: "Success!",
                description: res.message || "Patient report uploaded successfully.",
                status: "success",
                duration: 3000,
                isClosable: true,
            });

        } catch (err: any) {
            toast({
                title: "Upload failed.",
                description: err?.data?.message || "An error occurred while uploading.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsGenerating(false);
            handleClose();
        }
    };


    return (
        <Modal isOpen={open} onClose={handleClose} size="2xl" isCentered closeOnEsc={!isGenerating}
               closeOnOverlayClick={!isGenerating}>
            <ModalOverlay/>
            <ModalContent>
                <ModalHeader mb={'-30px'} color={textColor}>Upload Document</ModalHeader>
                <ModalCloseButton isDisabled={isGenerating} onClick={handleClose}/>

                <ModalBody>
                    <Flex flex="1" justify="center" align="center">
                        <Box
                            bg="white"
                            p={8}
                            textAlign="center"
                            width="100%"
                            maxW="700px"
                            height="300px"
                            display="flex"
                            flexDirection="column"
                            justifyContent="center"
                            alignItems="center"
                        >
                            {/* Instructional text */}
                            <Text fontSize="md" color="gray.500" mb={8} maxW="90%">
                                Upload a picture or PDF, or take a clear photo to generate a patient report.
                                Ensure the document includes diagnosis, prescribed treatment, recovery status,
                                and prescribed medication for accurate report creation.
                            </Text>

                            {/* Hidden file input */}
                            <Input
                                type="file"
                                ref={inputRef}
                                onChange={handleFileChange}
                                display="none"
                            />

                            {/* Show file name if selected */}
                            {fileName && (
                                <Text mb={6} color="gray.900" fontWeight="bold">
                                    Selected: {fileName}
                                </Text>
                            )}


                            <Box mt={'20px'} width={'100%'} display={'flex'} justifyContent={'space-around'}>
                                {/* Upload button */}
                                <Button
                                    leftIcon={<Icon as={FaUpload} boxSize={5}/>}
                                    onClick={handleUploadClick}
                                    isDisabled={isGenerating}
                                    bg={!fileName ? "#422AFB" : "#7363e4"}
                                    color="white"
                                    _hover={{bg: "#5842ff"}}
                                    // size="lg"
                                    width='250px'
                                    height={'50px'}
                                >
                                    {fileName ? "Change Document" : "Upload Document"}
                                </Button>

                                {/*submit button*/}
                                <Button
                                    colorScheme="blue"
                                    width="250px"
                                    height="50px"
                                    onClick={handleCreatePatientReport}
                                    isLoading={isLoading}
                                    isDisabled={!selectedFile}
                                >
                                    Create Patient Report
                                </Button>
                            </Box>
                            {isGenerating && (
                                <Text color="green.500" fontWeight="semibold" mt={6} fontSize="16">
                                    Generating patient report...
                                </Text>
                            )}
                        </Box>
                    </Flex>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default UploadDocumentModal;